import { API_BASE_URL } from '../../../envConfig';

/** Schema field row used by the Swagger-style dialog tables. */
export interface OpenApiFieldDoc {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string | number | null;
}

export interface OpenApiPathParamDoc {
  name: string;
  in: 'path';
  required: boolean;
  type: string;
  description: string;
  example: string;
}

export interface OpenApiResponseDoc {
  status: number;
  description: string;
  example?: Record<string, unknown>;
}

export interface AnalyticsIngestUnionExampleDoc {
  label: string;
  examplePayload: Record<string, unknown>;
}

export interface AnalyticsIngestOpenApiDoc {
  openapi: '3.0.3';
  info: {
    title: string;
    version: string;
    description: string;
  };
  servers: Array<{ url: string; description: string }>;
  pathTemplate: string;
  resolvedPath: string;
  method: 'POST';
  operationId: string;
  summary: string;
  description: string;
  securityNote: string;
  pathParams: OpenApiPathParamDoc[];
  bodyFields: OpenApiFieldDoc[];
  bodyOneOfNote: string;
  examplePayload: Record<string, unknown>;
  responses: OpenApiResponseDoc[];
  /** Panel-specific derivation notes (empty for the generic Settings dialog). */
  caveats: string[];
  /** Extra UNION branch payloads when a report uses UNION ALL. */
  unionExamples: AnalyticsIngestUnionExampleDoc[];
  /** high | partial | low — only set when example was derived from Report DSL. */
  deriveConfidence?: 'high' | 'partial' | 'low';
}

const SITE_ID_PLACEHOLDER = 'your-site-id';

const BODY_FIELDS: OpenApiFieldDoc[] = [
  {
    name: 'event_type',
    type: 'string',
    required: true,
    description:
      'Primary event name (LowCardinality). Examples: page_view, click, asset_archive_marked, asset_archive_completed. Defaults to "unknown" if omitted.',
    example: 'page_view',
  },
  {
    name: 'event_subtype',
    type: 'string | null',
    required: false,
    description:
      'Optional secondary classifier for the event (e.g. button_click, s3_upload, aem_metadata). Stored as Nullable(String).',
    example: null,
  },
  {
    name: 'language_iso_code',
    type: 'string',
    required: false,
    description:
      'Visitor / client language tag (ISO 639 or BCP 47), e.g. "en", "en-US", "fr". Empty string if unknown.',
    example: 'en',
  },
  {
    name: 'user_id',
    type: 'string',
    required: false,
    description:
      'Authenticated or known user identifier. For server-side ETL jobs, use a stable service account id (e.g. "etl-aem-s3-archiver").',
    example: '',
  },
  {
    name: 'anonymous_id',
    type: 'string',
    required: false,
    description:
      'Anonymous visitor / client device id (SDK cookie). For server-side callers, reuse a stable process id or leave empty.',
    example: '',
  },
  {
    name: 'session_id',
    type: 'string',
    required: false,
    description:
      'Session or run correlation id. Browser SDK sessions or ETL job/run ids both fit here.',
    example: '',
  },
  {
    name: 'page_url',
    type: 'string',
    required: false,
    description:
      'Full page URL or logical resource URI associated with the event (https://…, aem://…, s3://…).',
    example: 'https://example.com/about',
  },
  {
    name: 'country_code',
    type: 'string',
    required: false,
    description:
      'ISO 3166-1 alpha-2 country code. Server may override with GeoIP from the request IP when available.',
    example: 'US',
  },
  {
    name: 'device_type',
    type: 'string',
    required: false,
    description:
      'Device form factor (desktop, mobile, tablet) or "server" for backend/ETL ingest.',
    example: 'desktop',
  },
  {
    name: 'device_os',
    type: 'string',
    required: false,
    description:
      'OS family: iOS, Android, Windows, macOS, Linux, ChromeOS, Other — or "etl" for server jobs.',
    example: 'macOS',
  },
  {
    name: 'utm_source',
    type: 'string | null',
    required: false,
    description: 'Marketing utm_source campaign parameter when applicable; otherwise null.',
    example: null,
  },
  {
    name: 'custom_dimensions',
    type: 'object (map of string → string)',
    required: false,
    description:
      'Arbitrary string eVars (e.g. asset_id, asset_path, job_id, source). Keys must be alphanumeric/underscore/hyphen. Values appear in reports as custom_dimensions.<key>; asset_id is also materialized as a column.',
    example: '{"asset_id":"asset-123","source":"web-sdk"}',
  },
  {
    name: 'custom_metrics',
    type: 'object (map of string → number)',
    required: false,
    description:
      'Arbitrary numeric metrics (e.g. asset_size_bytes, duration_ms, value). Values are Float64 in ClickHouse.',
    example: '{"value":1}',
  },
  {
    name: 'latitude',
    type: 'number',
    required: false,
    description:
      'Optional precise latitude (WGS84). Only send when geolocation is consented; omit or leave unset when unknown. Server defaults missing values to 0.',
    example: 0,
  },
  {
    name: 'longitude',
    type: 'number',
    required: false,
    description:
      'Optional precise longitude (WGS84). Paired with latitude; omitted/unknown defaults to 0 server-side.',
    example: 0,
  },
  {
    name: 'location_accuracy',
    type: 'number',
    required: false,
    description:
      'Reported geolocation accuracy in metres (from navigator.geolocation). Optional; defaults to 0 when absent.',
    example: 0,
  },
];

const resolveSiteIdExample = (siteId: string | undefined | null): string => {
  const trimmed = (siteId || '').trim();
  return trimmed || SITE_ID_PLACEHOLDER;
};

const buildExamplePayload = (): Record<string, unknown> => ({
  event_type: 'page_view',
  event_subtype: null,
  language_iso_code: 'en',
  user_id: '',
  anonymous_id: 'anon-example-uuid',
  session_id: 'session-example-uuid',
  page_url: 'https://example.com/about',
  country_code: 'US',
  device_type: 'desktop',
  device_os: 'macOS',
  utm_source: null,
  custom_dimensions: {
    asset_id: 'asset-123',
    source: 'web-sdk',
  },
  custom_metrics: {
    value: 1,
  },
  latitude: 0,
  longitude: 0,
  location_accuracy: 0,
});

export interface BuildAnalyticsIngestOpenApiOptions {
  tenantId?: string | null;
  siteId?: string | null;
  /** Override API server URL (defaults to API_BASE_URL). */
  serverUrl?: string;
  /** Appended to the dialog title when documenting a specific report panel. */
  titleSuffix?: string;
  /** Replace the generic example body (e.g. Report-DSL-derived payload). */
  examplePayloadOverride?: Record<string, unknown>;
  /** Derivation / invertibility notes shown in the dialog. */
  caveats?: string[];
  /** Extra UNION branch example payloads. */
  unionExamples?: AnalyticsIngestUnionExampleDoc[];
  deriveConfidence?: 'high' | 'partial' | 'low';
}

/**
 * Builds an OpenAPI 3.0-shaped documentation object for
 * POST /il/analytics/{tenant_id}/{site_id}/events with path params
 * pre-filled from the ClickHouse module configuration.
 */
export const buildAnalyticsIngestOpenApi = (
  opts: BuildAnalyticsIngestOpenApiOptions = {},
): AnalyticsIngestOpenApiDoc => {
  const tenantExample = (opts.tenantId || '').trim() || 'your-tenant-id';
  const siteExample = resolveSiteIdExample(opts.siteId);
  const isTenantWide = !(opts.siteId || '').trim();
  const serverUrl = (opts.serverUrl || API_BASE_URL || '').replace(/\/$/, '');
  const pathTemplate = '/il/analytics/{tenant_id}/{site_id}/events';
  const resolvedPath = `/il/analytics/${encodeURIComponent(tenantExample)}/${encodeURIComponent(siteExample)}/events`;
  const titleSuffix = (opts.titleSuffix || '').trim();
  const title = titleSuffix
    ? `Analytics Event Ingest API — ${titleSuffix}`
    : 'Analytics Event Ingest API';
  const panelBlurb = titleSuffix
    ? ` Example payload below is derived from the Report DSL for panel "${titleSuffix}" so ingested events contribute to that panel when filters match.`
    : '';

  return {
    openapi: '3.0.3',
    info: {
      title,
      version: '1.0.0',
      description:
        'Public ingest endpoint used by the Infralytiqs SDK and server-side clients to push events into ClickHouse `website_events`.'
        + panelBlurb,
    },
    servers: [
      {
        url: serverUrl || '/',
        description: 'API gateway (from client environment)',
      },
    ],
    pathTemplate,
    resolvedPath,
    method: 'POST',
    operationId: 'ingestAnalyticsEvents',
    summary: 'Ingest one or more analytics events',
    description:
      'Accepts a single event object or an array of events (max 1000). '
      + '`tenant_id` and `site_id` are taken from the URL path and injected into each row — '
      + 'do not rely on body fields for scoping. Auth is the configured tenant_id + site_id pair '
      + 'stored under Infralytiqs Settings → ClickHouse module configuration (no session cookie required).',
    securityNote:
      'No user session or Bearer token. The URL path tenant_id + site_id must match a ClickHouse '
      + 'moduleConfiguration in IL-Settings. Invalid pairs return HTTP 403.',
    pathParams: [
      {
        name: 'tenant_id',
        in: 'path',
        required: true,
        type: 'string',
        description:
          'Infralytiqs tenant id from the ClickHouse module configuration. Pre-filled from this settings page.',
        example: tenantExample,
      },
      {
        name: 'site_id',
        in: 'path',
        required: true,
        type: 'string',
        description: isTenantWide
          ? 'Site id written into ClickHouse. This ClickHouse config is tenant-wide (blank Site ID), '
            + 'so any site_id segment is accepted; substitute your logical site id (placeholder shown).'
          : 'Site id from the ClickHouse module configuration. Pre-filled from this settings page.',
        example: siteExample,
      },
    ],
    bodyFields: BODY_FIELDS,
    bodyOneOfNote:
      'Content-Type: application/json. Body is either a single AnalyticsEvent object or an array of '
      + 'AnalyticsEvent objects (1–1000 items). Empty arrays and batches over 1000 are rejected with HTTP 400. '
      + 'Body fields named tenant_id / site_id (if sent) are ignored — path params win.',
    examplePayload: opts.examplePayloadOverride || buildExamplePayload(),
    caveats: opts.caveats || [],
    unionExamples: opts.unionExamples || [],
    deriveConfidence: opts.deriveConfidence,
    responses: [
      {
        status: 201,
        description: 'Events ingested successfully',
        example: {
          success: true,
          message: '1 event(s) ingested successfully',
          count: 1,
        },
      },
      {
        status: 400,
        description: 'Invalid body (empty batch, >1000 events, or malformed JSON)',
        example: { error: 'At least one event is required' },
      },
      {
        status: 403,
        description: 'No matching ClickHouse module configuration for tenant_id / site_id',
        example: {
          error: 'Invalid tenant_id or site_id. No matching ClickHouse configuration found.',
        },
      },
      {
        status: 500,
        description: 'ClickHouse write or internal server failure',
        example: { error: 'Failed to ingest events: …' },
      },
    ],
  };
};

/** Absolute example URL for curl / client docs. */
export const buildAnalyticsIngestAbsoluteUrl = (doc: AnalyticsIngestOpenApiDoc): string => {
  const base = (doc.servers[0]?.url || '').replace(/\/$/, '');
  return `${base}${doc.resolvedPath}`;
};
